/**
 * English strings for the signup + pre-checkout onboarding flow (PREC-3653).
 * Kept out of en.ts to keep the main translation file within the size gate.
 */
export const enRegister = {
	createYourAccount: "Create Your Account",
	yourFirst: "Your first ",
	freePersonalLeadLists: "free personalised lead lists",
	areJust: "are just",
	aFewClicksAway: "a few clicks away. The first 200 credits are",
	onUs: "on us.",
	yourBusinessEmailAddress: "Your business email address",
	yourBusinessEmailAddressPlaceholder: "yourname@company.com",
	yourLinkedInProfile: "Your LinkedIn profile",
	yourLinkedInProfilePlaceholder: "https://www.linkedin.com/in/yourname",
	password: "Password",
	passwordPlaceholder: "At least 8 characters",
	signupForFree: "Signup for free",
	alreadyHaveAnAccount: "Already have an account? ",
	login: "Login",
};

export const enOnboarding = {
	/** The way out of the onboarding flow — see OnboardingExit (PREC-3976). */
	exit: {
		signedInAs: "Signed in as {{email}}.",
		logout: "Log out",
	},
	lists: {
		preparingTitle: "Preparing your first lists",
		preparingSubtitle: "Picking the list types that fit your targeting — this takes a moment.",
		titleFeatured: "Build your first lists",
		titleCatalog: "Build your own lists",
		subtitleFeatured: "Recommended for you, based on your targeting — adjust anything you like.",
		subtitleCatalog:
			"Pick up to two list types to start with — you can do even more inside Signalist.",
		buildOther: "I want to build other lists",
		backToRecommended: "Back to recommendations",
		pickedCount: "{{count}}/{{max}} lists picked",
		maxToast: "You can pick up to 2 list types during onboarding — please deselect one first",
		fixFieldsToast: "Please complete the highlighted fields before continuing",
		pickOneToast: "Please pick at least one list type to continue",
		createFailed: "Creating your lists failed — please try again",
		recommendationFailed:
			"We couldn't work out your recommendations, so the fields below start empty.",
		retryRecommendation: "Try again",
		recommendationRetrying: "Loading\u2026",
		recommendationReady: "Your recommendations are ready.",
		applyRecommendation: "Apply them",
		continue: "Next: Add target accounts →",
		types: {
			hiring: {
				title: "Find companies that are hiring",
				description: "For recruiting and staffing: find companies hiring for the roles you place",
			},
			lookalike: {
				title: "Find lookalike companies",
				description: "Surface companies similar to your best customers",
			},
			leadDb: {
				title: "Search the lead database",
				description: "Filter our database by role, seniority, industry, size and location",
			},
			topic: {
				title: "Find people posting about a topic",
				description: "Capture people posting or engaging about your topics on LinkedIn",
			},
			newInRole: {
				title: "Find people that started a new role",
				description: "Reach people in their first weeks — when budgets and tools get rethought",
			},
			gmaps: {
				title: "Import Google Maps",
				description: "Find local businesses on Google Maps and relevant leads at those",
			},
			aiCompany: {
				title: "Find companies with natural language",
				description: "Describe the companies you want and let our AI search the web for them",
			},
			aiNews: {
				title: "Monitor company news and announcements",
				description: "Describe the news to watch and get the matching companies continuously",
			},
			funding: {
				title: "Track startup funding rounds",
				description: "Companies that just raised — fresh budget and a reason to talk",
			},
			mna: {
				title: "Track Mergers & Acquisitions",
				description: "Companies that merged or were acquired, plus relevant leads there",
			},
		},
		sections: {
			linkedinActivity: "LinkedIn activity",
			leadDatabases: "Lead databases",
			news: "News",
			jobs: "Jobs",
			fundingMa: "Funding & M&A",
		},
		config: {
			hiringFor: "Hiring for",
			hiringForPlaceholder: "e.g. SDR, Account Executive",
			location: "Location",
			locationPlaceholder: "Start typing a country or city\u2026",
			employmentType: "Employment type",
			employmentFulltime: "Full-time",
			employmentParttime: "Part-time",
			employmentContractor: "Contractor",
			employmentIntern: "Intern",
			employmentTemporary: "Temporary",
			findTitles: "At those companies find",
			findTitlesPlaceholder: "Add a job title\u2026",
			similarTo: "Similar to (domains)",
			similarToPlaceholder: "e.g. hubspot.com",
			keywords: "Company keywords",
			// PREC-3896: the field said what it was called and never what it did. Keywords are matched
			// literally against the company record, so each one narrows the search — the reason they
			// are never prefilled from the AI recommendation either (see onboardingListDefaults).
			keywordsHint: "Matched word for word on the company — each keyword narrows the search",
			semanticQuery: "Semantic search",
			semanticQueryHint: "(optional)",
			semanticQueryPlaceholder: "Describe the companies in your own words\u2026",
			keywordsPlaceholder: "e.g. B2B, remote-first",
			seniority: "Seniority",
			seniorityPlaceholder: "Select seniority levels",
			department: "Department",
			departmentPlaceholder: "Select departments",
			personLocation: "Person location",
			personLocationPlaceholder: "Select countries",
			industries: "Industries",
			industriesPlaceholder: "Select industries",
			employees: "Employee size",
			employeesPlaceholder: "Select employee size",
			companyHq: "Company HQ",
			companyHqPlaceholder: "Select company HQ",
			changeTypes: "Job change",
			changeTypeNewRole: "Started a new role",
			changeTypeNewEmployer: "Joined a new employer",
			startedWithin: "Started within",
			startedWithin30: "the last 30 days",
			startedWithin90: "the last 90 days",
			startedWithin180: "the last 180 days",
			topic: "Topic",
			topicPlaceholder: "e.g. sales automation",
			authorJobTitle: "Author job title",
			authorJobTitlePlaceholder: "e.g. Head of Sales",
			timeFrame: "Posted within",
			timeFrame7: "the last 7 days",
			timeFrame30: "the last 30 days",
			timeFrame90: "the last 90 days",
			capture: "Capture",
			capturePosts: "The people posting",
			captureLikes: "People who liked a post",
			captureComments: "People who commented on a post",
			businessType: "Business type",
			businessTypePlaceholder: "e.g. marketing agencies, dentists",
			country: "Country",
			countryPlaceholder: "e.g. Germany",
			city: "City",
			cityPlaceholder: "e.g. Berlin",
			companyDescription: "Describe the companies you want",
			companyDescriptionPlaceholder:
				"e.g. B2B SaaS startups in Berlin, 20\u2013100 employees, selling to HR",
			newsQuery: "Describe the news to watch",
			newsQueryPlaceholder: "e.g. opened a new production site in Europe",
			frequency: "Check",
			frequencyDaily: "Daily",
			frequencyWeekly: "Weekly",
			fundingCountries: "Countries",
			fundingCountriesPlaceholder: "Select countries",
			fundingCategory: "Sectors",
			fundingCategoryPlaceholder: "Select sectors",
			fundingRound: "Funding round",
			fundingRoundPlaceholder: "Select rounds",
			lastXDays: "Announced within",
			lastXDays30: "the last 30 days",
			lastXDays90: "the last 90 days",
			lastXDays365: "the last 12 months",
			peopleAtCompany: "At each company, find",
			peopleAtCompanyHint:
				"These companies only get you the account \u2014 tell us who we should look for inside them.",
			positionCeo: "The CEO and similar leadership roles",
			positionTitle: "A specific job title",
			positionDepartment: "A department & seniority",
			jobTitle: "Job title",
			jobTitlePlaceholder: "e.g. Head of Sales",
			peoplePerCompany: "People per company",
			peoplePerCompanyHint: "Each person found costs credits when the list runs.",
			errors: {
				required: "Required",
				requiredAnyFilter: "Add at least one filter so the list stays targeted",
				maxDomains: "At most {{count}} domains",
				maxSemanticQuery: "At most {{count}} characters",
			},
		},
	},
	track: {
		preparingTitle: "Finding people to track",
		preparingSubtitle: "Matching your targeting against the people database — this takes a moment.",
		title: "Track your target accounts",
		subtitle:
			"Signalist watches these for buying signals and alerts you the moment intent shows up. We pre-selected matches for your targeting — unselect any you don't care about.",
		selectedCount: "{{count}}/max {{max}} picked",
		minHint: "Pick at least {{min}} to continue",
		colName: "Name",
		colCompany: "Company",
		colPosition: "Position",
		colDepartment: "Department",
		findMorePeople: "Find more people",
		bulkNoteTitle: "Adding people one by one is just the start.",
		bulkNote:
			"Inside Signalist you can upload a list of people (name + company), upload a list of companies and let us find the right people at them, or search by job title.",
		loadingMatches: "Loading matches\u2026",
		removeRow: "Remove from list",
		filterPeopleTitle: "Find different people",
		filterPosition: "Position / Job Title",
		filterPositionPlaceholder: "e.g. VP Sales, RevOps",
		filterSeniority: "Seniority",
		filterSeniorityPlaceholder: "Select seniority levels",
		filterDepartment: "Department",
		filterDepartmentPlaceholder: "Select departments",
		filterLocation: "Personal location",
		filterLocationPlaceholder: "Select countries",
		filterIndustry: "Industry",
		filterIndustryPlaceholder: "Select industries",
		filterCompanySize: "Company size",
		filterCompanySizePlaceholder: "Select employee ranges",
		filterCompanyLocation: "Company location",
		filterCompanyLocationPlaceholder: "Select countries",
		filterSemantic: "Describe the companies",
		filterSemanticPlaceholder: "Companies you want to reach\u2026",
		filterSkills: "Skills & keywords",
		filterSkillsPlaceholder: "e.g. RevOps, ABM",
		filterKeywords: "Company keywords",
		filterKeywordsPlaceholder: "e.g. logistics software",
		filterSearch: "Search",
		relaxedToSeniority:
			"No exact matches for your job titles — we widened the search to the seniority levels behind them.",
		relaxedToBroad:
			"No exact matches for your job titles — we widened the search to those seniority levels across every company that fits your targeting.",
		emptyTitle: "No people matched these filters",
		failedTitle: "We couldn't load suggestions",
		failedHint: "Your filters are fine — the search itself did not come back.",
		failedRetry: "Try again",
		emptyHint:
			"Widen your filters under “Find different people”, or paste a LinkedIn profile below.",
		addPerson: "Add a person",
		addPersonPlaceholder: "Search by name, or paste a LinkedIn profile URL\u2026",
		addPersonUseUrl: "Use this LinkedIn profile",
		addPersonNoResults:
			"No one found \u2014 try another spelling, or paste their LinkedIn profile URL.",
		back: "Back",
		maxToast: "You can select up to {{max}} companies or people during onboarding",
		continue: "Next: Set your priorities →",
		loadFailed:
			"We couldn't load suggestions right now — you can add target accounts later inside Signalist.",
		createFailed: "Creating your tracker failed — please try again",
		interTitle: "Nice — you're all set!",
		interSubtitle: "For every company you picked, Signalist gets to work behind the scenes.",
		interStep1Title: "We search the web continuously",
		interStep1Text: "Monitoring news, hiring, funding and leadership changes as they happen.",
		interStep2Title: "We pull their LinkedIn posts & open jobs",
		interStep2Text: "Surfacing the signals that actually matter for your outreach.",
		interStep3Title: "You reach out in one click",
		interStep3Text: "Once your LinkedIn or email is connected, send at exactly the right moment.",
		letsGo: "Let's go →",
	},
	prime: {
		opener: "Thanks for your target list — I'll get to work. 🙌",
		whenQuestionPrefix:
			"Oh, by the way — for the leads and companies you just added, I'd assume these would probably be a good reason to reach out:",
		whenQuestionSuffix: "Correct?",
		whenQuestionNoDraft:
			"Before we go on — which signals at your target accounts should I be monitoring? Tell me what would give you a good reason to reach out.",
		whenPlaceholder: "Which signals should I monitor instead?",
		whenAckConfirmed: "Perfect — I'll prioritize those triggers.",
		whenAckEdited: "Updated — I'll listen for that instead.",
		whyQuestion:
			"And if I had to say why a lead should talk to you — why you, why now — I'd put it like this. Tweak it so it sounds like you:",
		whyPlaceholder: "Why should a lead talk to you?",
		whyAckConfirmed: "Love it — that's exactly the angle I'll write from.",
		whyAckEdited: "Love it — much sharper. That's what I'll write from.",
		whatQuestionPrefix: "Last one — to prep you for each conversation, I'll dig up:",
		whatQuestionSuffix: "…and surface it in the Lead Panel. Should I research anything different?",
		whatQuestionNoDraft:
			"Last one — to prep you for each conversation, what should I dig up about an account before a call?",
		whatPlaceholder: "What should I research on each account?",
		whatAckConfirmed: "Locked in. Setting everything up now…",
		whatAckEdited: "Got it — I'll research exactly that. Setting everything up…",
		chipLooksGood: "Looks good",
		chipChangeSignals: "Change which signals I should be monitoring",
		chipChangeResearch: "Change what I research",
		chipContinue: "Continue",
	},
	chat: {
		richIntro:
			"Based on what I know about {{company}} and your profile, I assume you primarily target",
		at: "at",
		companies: "companies",
		in: "in",
		confirmQuestion: "Does this sound correct?",
		chipYes: "Yes, that's right!",
		chipAdjust: "Almost — let me adjust",
		confirmedIntro: "Great — glad I got that right! I'll target",
		updatedIntro: "Got it — I've updated your targeting to:",
		feedsNote:
			"This is what I build your Lead Scoring, your Knowledge Base and your Deep Research from — and you can change every part of it later inside Signalist.",
		adjustPrompt:
			"No problem! Tell me what you'd change — different personas, industries, company sizes or regions.",
		adjustPlaceholder: "Tell me what to change…",
		adjustFailed: "Hmm, that didn't work — could you try phrasing it differently?",
		adjustNoChange:
			'Hmm — that didn\'t change anything on my side. Could you spell it out a bit more, e.g. "target the whole world, not just Europe" or "enterprises instead of startups"?',
		thinOpener1:
			"I'm pulling together what {{company}} does in the background — it'll take me a moment.",
		thinOpener2:
			"While I do, it'd help to hear it from you: in one line, who are your best customers?",
		thinExample: 'e.g. "VP Sales at B2B SaaS scale-ups in DACH" — a rough sketch is plenty.',
		thinPlaceholder: "Describe your ideal customers…",
		thinAck:
			"Thanks — that sharpens it up. Combining your note with what I found, here's the ICP I'd start from:",
		generationTimeout:
			"This is taking longer than expected — I'll keep refining your profile in the background. You can adjust your targeting anytime later.",
		bothIntro:
			"Signalist helps you do both: find new leads and track signals on relevant target accounts so you can nurture them at the right moment. Here are a few ideas that could be useful for you:",
		bothBullet1: "Find the decision makers that match your ideal customer profile",
		bothBullet2: "Surface lookalikes of your best customers",
		bothBullet3: "Track when people at your target accounts post about your topics",
		bothBullet4: "Get alerted when a champion moves to a new target account",
		bothIntroPersonal:
			"Signalist helps you do both: find new leads and track signals on relevant target accounts so you can nurture them at the right moment. Based on your targeting, here's what I'd start with for you:",
		bothTrackLine:
			"…and right after that, we'll pick the target accounts I should watch for buying signals",
		listPreview: {
			hiring: "Companies hiring {{role}} right now — hiring is a strong buying signal",
			lookalike: "Companies that look like {{domains}}",
			leadDb: "{{titles}} at {{industries}} companies",
			topic: "People posting about {{topic}} on LinkedIn",
			newInRole: "{{titles}} who just started a new role",
			gmaps: "{{businessType}} in your region, straight from Google Maps",
			aiCompany: "Companies matching: {{description}}",
			aiNews: "Companies where: {{news}}",
			funding: "Companies that just raised {{rounds}}",
			mna: "Companies that merged or were acquired in {{sectors}}",
		},
		chipStart: "OK, let's get started building some lead lists",
		startAck: "Perfect — let's build your first lists.",
		// PREC-3896: "name two or three that are alike" did not land — say what the seeds are used
		// for, so the constraint follows from the mechanic instead of being a rule to obey.
		customersQuestion:
			"One more thing: which companies are your best customers today? Name two or three of the same kind — I run a single search for companies that look like all of them together, so seeds that resemble each other give the sharpest matches.",
		customersExample:
			'e.g. "HubSpot, Personio and Celonis" — three B2B software vendors, rather than one of each kind.',
		customersPlaceholder: "Name a few customers…",
		chipSkipCustomers: "I'd rather skip this",
		customersAck: "Got it — I'll use these to find lookalikes:",
		customersAckNone: "Thanks — you can always add reference customers later.",
		seeds: {
			hint: "Remove anything that doesn't fit and add what I missed. They should resemble each other — a research institute next to a software agency has no useful lookalikes between them.",
			// PREC-3896: shown when the extraction read the named companies as different kinds of
			// business. A warning, never a block — the user may know better than the extraction did.
			mixedWarning:
				"These look like different kinds of company ({{kinds}}). They all go into one lookalike search, so it returns a bit of each — keep the ones that resemble each other and add more of that kind.",
			addPlaceholder: "Add another company…",
			remove: "Remove {{name}}",
			confirm: "These are right",
		},
	},
	// The slim onboarding (V3, PREC-3719): joining a workspace someone else set up.
	slimStep: {
		title: "Welcome aboard!",
		titleWithName: "Welcome aboard, {{name}}!",
		subtitle:
			"Your team already set up Signalist — we only need your own LinkedIn profile, so your outreach goes out as you.",
		continue: "Continue →",
	},
	profileStep: {
		title: "Great start!",
		titleWithName: "Great start, {{name}}!",
		subtitle:
			"Let's set you up — we pre-filled what we could from your email domain. Just confirm everything looks right.",
		personalLinkedinLabel: "Your personal profile on LinkedIn (Optional)",
		personalLinkedinUrlLabel: "Your LinkedIn profile URL",
		typeaheadPlaceholder: "Your name or your LinkedIn profile URL…",
		useTypedUrl: "Use this LinkedIn profile URL",
		pasteProfileUrlHint: "Can't find yourself? Paste your LinkedIn profile URL here.",
		clearSelection: "Clear",
		companyWebsiteLabel: "Your company website",
		companyWebsitePlaceholder: "Enter your company website",
		companyWebsiteInfo:
			"We need this to increase the accuracy of your lead scoring. We will analyze the website to get the latest information about your company.",
		whyWeNeedThis: "Why we need this",
		howToFind: "How to find",
		companyLinkedinLabel: "Your Company's profile on LinkedIn",
		companyLinkedinUrlLabel: "Your company's LinkedIn URL",
		companyTypeaheadPlaceholder: "Start typing your company name…",
		cantFindCompany: "+ I can't find my company — enter LinkedIn URL",
		companyLinkedinPlaceholder: "Enter your LinkedIn URL",
		companyLinkedinInfoBefore: "Go to",
		companyLinkedinInfoAfter:
			'and click on the name of your company in the section "My pages" on the left, below the section of your profile.',
		continue: "Next: Confirm your targeting →",
	},
	done: {
		startFailed: "We couldn't start your workspace — please try again.",
	},
};
